import { requireCompletedProfile } from "@hisani/auth";
import { vaccinationAlertSeverity } from "../domain/vaccinations/vaccination-alert.js";
import { formatCalendarDayUTC } from "../domain/vaccinations/vaccination-interval.js";
import { listStableVaccinationAlertsSchema } from "../validation.js";
import { requireFeatureMembership } from "./sealed-record-support.js";
/**
 * The overdue/due-soon flag surfaced on a Stable's horse list and horse
 * detail page — a live, read-time recomputation of
 * `vaccinationAlertSeverity` over each horse's latest logged dose
 * (`listStableVaccinationNextDueDates`, alerting-enabled vaccine types
 * only), never the dedup tracking table the daily scan owns. Two callers
 * reading the same `nextDue` at different lead times (the daily scan's
 * notification cycle vs. this always-live flag) can briefly disagree, by
 * design — `vaccination-alert.ts`'s own doc comment: "the flag ... is never
 * stored; it is recomputed on every read."
 */
export async function listStableVaccinationAlerts(principal, input, dependencies) {
    const actor = requireCompletedProfile(principal);
    const parsed = listStableVaccinationAlertsSchema.parse(input);
    await requireFeatureMembership(dependencies, parsed.stableId, actor.account.id, "VACCINATIONS", "VIEW");
    const [rows, policy] = await Promise.all([
        dependencies.repository.listStableVaccinationNextDueDates(parsed.stableId),
        dependencies.policy.get(),
    ]);
    const today = formatCalendarDayUTC(dependencies.clock.now());
    const alerts = [];
    for (const row of rows) {
        if (row.nextDue === null)
            continue;
        const config = policy.vaccineTypes[row.vaccineType];
        if (config === undefined)
            continue;
        const severity = vaccinationAlertSeverity({
            nextDue: row.nextDue,
            today,
            warningLeadDays: policy.vaccinationAlertWarningLeadDays,
            nudgeLeadDays: policy.vaccinationAlertNudgeLeadDays,
        });
        if (severity === "NONE")
            continue;
        alerts.push({
            horseId: row.horseId,
            vaccineType: row.vaccineType,
            vaccineTypeLabel: config.label,
            nextDue: row.nextDue,
            severity,
        });
    }
    return alerts;
}
